import { useTranslation } from 'react-i18next';

import { BLOOD_TYPE_LABELS, medicDataAge } from '@ngsos/portal-core/incidents';
import { DataPanel, DataPanelAutoWrapInlineItem, DataPanelItem, DataPanelRow, DebugView } from '@/ui';
import type { Caller, EmergencyContact } from '@/signalr';
import { ClosePersonContactPanelItem } from '../shared/ClosePersonContactPanelItem';
import { GoogleElsDetectionsPanelItem } from './GoogleElsDetectionsPanelItem';
import { GoogleElsDiagnosticsPanelItems } from './GoogleElsDiagnosticsPanelItems';

type GoogleElsSource = NonNullable<Caller['dataSources']['currentGoogleEls']>;
type GoogleElsMedicData = NonNullable<GoogleElsSource['medicData']>;

export type GoogleElsSectionProps = {
  source: Caller['dataSources']['currentGoogleEls'];
  createdAtUtc: string;
};

function withNote(value: string | null, note: string | null): string | undefined {
  const parts = [value, note].filter((part): part is string => part != null && part.trim() !== '');
  return parts.length === 0 ? undefined : parts.join(' · ');
}

function toEmergencyContacts(contacts: GoogleElsSource['contacts']): EmergencyContact[] {
  return contacts
    .filter((contact) => contact.phoneNumber != null)
    .map((contact) => ({
      name: contact.name ?? '',
      phoneNumber: contact.phoneNumber!,
      email: null,
      relationship: contact.relationship,
    }));
}

function GoogleElsMedicDataPanelItems({ medicData }: { medicData: GoogleElsMedicData }) {
  const { t } = useTranslation();

  return (
    <>
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_Name')}
        value={medicData.name ?? undefined}
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_PermanentResidence')}
        value={medicData.homeAddress ?? undefined}
        showEmptyPlaceholder
      />

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_AppleEed_Allergies')}
        value={medicData.allergies ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_AppleEed_Medications')}
        value={medicData.medications ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_AdvanceDirectives')}
        value={medicData.advancedDirectives ?? undefined}
        nonEmptyValueColor="critical"
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_MedicData_Pregnancy')}
        value={
          medicData.pregnancyStatus === 'Unknown'
            ? withNote(null, medicData.pregnancyNote)
            : withNote(
                [t(`Incident_InfoPanel_MedicData_PregnancyStatus_${medicData.pregnancyStatus}`), medicData.pregnancyDueDate]
                  .filter((part): part is string => part != null && part !== '')
                  .join(' · '),
                medicData.pregnancyNote,
              )
        }
        nonEmptyValueColor={medicData.pregnancyStatus === 'Pregnant' ? 'critical' : undefined}
        showEmptyPlaceholder
      />

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_MedicData_ConditionsDescription')}
        value={medicData.conditionsHistory ?? undefined}
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_MedicData_AssistanceDescription')}
        value={medicData.devicesAndMobility ?? undefined}
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_OrganDonation')}
        value={withNote(
          medicData.organDonor === 'Unknown' ? null : t(`Incident_InfoPanel_OrganDonation_${medicData.organDonor}`),
          medicData.organDonorNote,
        )}
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_GoogleEls_PhysicianInfo')}
        value={medicData.physicianInfo ?? undefined}
        showEmptyPlaceholder
      />
      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_CallerNotes')}
        value={medicData.other ?? undefined}
        showEmptyPlaceholder
      />
    </>
  );
}

function GoogleElsPersonRows({ medicData, createdAtUtc }: { medicData: GoogleElsMedicData; createdAtUtc: string }) {
  const { t } = useTranslation();
  const yearOfBirth = medicData.dateOfBirth == null ? null : Number.parseInt(medicData.dateOfBirth.slice(0, 4), 10);

  return (
    <>
      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_Age')}
          value={withNote(
            medicDataAge(Number.isNaN(yearOfBirth) ? null : yearOfBirth, createdAtUtc)?.toString() ?? null,
            medicData.dateOfBirthNote,
          )}
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_CallerHeight')}
          value={medicData.height ?? undefined}
          contentCssClass="text-center"
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_CallerWeight')}
          value={medicData.weight ?? undefined}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>

      <DataPanelRow>
        <DataPanelItem
          title={t('Incident_InfoPanel_Sex')}
          value={withNote(
            medicData.sex === 'Unknown' ? medicData.gender : t(`Incident_InfoPanel_Sex_${medicData.sex}`),
            medicData.sexNote,
          )}
          showEmptyPlaceholder
        />
        <DataPanelItem
          title={t('Incident_InfoPanel_AppleEed_BloodType')}
          value={withNote(BLOOD_TYPE_LABELS[medicData.bloodType] ?? null, medicData.bloodTypeNote)}
          contentCssClass="text-end"
          showEmptyPlaceholder
        />
      </DataPanelRow>
    </>
  );
}

export function GoogleElsSection({ source, createdAtUtc }: GoogleElsSectionProps) {
  const { t } = useTranslation();

  if (source == null) {
    return null;
  }

  const emergencyType =
    source.detections === null || source.detections.emergencyType === 'Unknown'
      ? null
      : source.detections.emergencyType;

  return (
    <DataPanel header="Google ELS data">
      <GoogleElsDetectionsPanelItem detections={source.detections} />

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_EmergencyType')}
        value={emergencyType ?? undefined}
        showEmptyPlaceholder
      />

      {source.medicData != null && (
        <>
          <GoogleElsPersonRows medicData={source.medicData} createdAtUtc={createdAtUtc} />
          <GoogleElsMedicDataPanelItems medicData={source.medicData} />
        </>
      )}

      <DataPanelAutoWrapInlineItem
        title={t('Incident_InfoPanel_Languages')}
        value={source.device?.languages.join(', ')}
        showEmptyPlaceholder
      />

      <ClosePersonContactPanelItem contacts={toEmergencyContacts(source.contacts)} />

      <DebugView>
        <GoogleElsDiagnosticsPanelItems source={source} />
      </DebugView>
    </DataPanel>
  );
}
